import { useState } from "react";
import { Button, Card, Empty, Space, Typography } from "antd";
import { FileExcelOutlined, FileImageOutlined, FileOutlined, FilePdfOutlined, FileWordOutlined, UploadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { EvidenceDto, EvidenceTargetType } from "@audit-plan/shared";
import { usePeople } from "../../api/people";
import { ROLE_ORDER, evidenceRole, fileExt } from "../../api/evidence";
import { useAudits } from "../../api/audits";
import { useIsMobile } from "../useIsMobile";
import { EvidenceStatusPill, fmtDT } from "../pills";
import { palette } from "../../theme";
import { UploadModal, useTargetLabel } from "./UploadModal";
import { EvidenceDrawer, fmtBytes } from "./EvidenceDrawer";

const icon = (name: string) => {
  const e = fileExt(name);
  const st = { fontSize: 24, color: palette.primary };
  if (e === "pdf") return <FilePdfOutlined style={st} />;
  if (e === "docx") return <FileWordOutlined style={st} />;
  if (e === "xlsx") return <FileExcelOutlined style={st} />;
  if (["jpg", "jpeg", "png", "heic", "heif"].includes(e)) return <FileImageOutlined style={st} />;
  return <FileOutlined style={st} />;
};

/** Prima riga della descrizione senza il ruolo: la provenienza («allegato del messaggio …», «sottocartella …»). */
const provenance = (description?: string | null) => {
  const first = (description ?? "").split("\n")[0] ?? "";
  const i = first.indexOf(" - ");
  return i >= 0 ? first.slice(i + 3) : "";
};

/**
 * Elenco delle evidenze, lo stesso nell'audit, nel fornitore e nello stabilimento: raggruppato per ruolo (rapporto, esito,
 * azioni correttive, altri documenti) quando i ruoli ci sono, poi per nome. bare = senza riquadro né caricamento (righe audit).
 */
export function EvidencePanel(props: {
  evidence: EvidenceDto[];
  ctx: { supplierId: string; auditId?: string | null; target?: { type: EvidenceTargetType; id: string } };
  canUpload: boolean;
  onChanged?: () => void;
  showAudit?: boolean;
  bare?: boolean;
}) {
  const { evidence: raw, ctx, canUpload, onChanged, showAudit, bare } = props;
  const evidence = [...raw].sort((a, b) => ROLE_ORDER.indexOf(evidenceRole(a.description)) - ROLE_ORDER.indexOf(evidenceRole(b.description)) || a.name.localeCompare(b.name));
  const groups = ROLE_ORDER.map((r) => ({ role: r, items: evidence.filter((e) => evidenceRole(e.description) === r) })).filter((g) => g.items.length);
  const grouped = groups.length > 1 || (groups[0] && groups[0].role !== "other");
  const { t } = useTranslation();
  const people = usePeople();
  const label = useTargetLabel(ctx.auditId);
  const audits = useAudits({}, !!showAudit); // solo per i codici audit quando showAudit
  const mobile = useIsMobile();
  const [up, setUp] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const live = evidence.find((e) => e.id === openId) ?? null;
  const auditCode = (id: string | null) => (id && showAudit ? audits.data?.find((a) => a.id === id)?.code : undefined);

  const row = (e: EvidenceDto) => {
    const v = e.versions[e.versions.length - 1];
    const prov = provenance(e.description);
    // l'utente di servizio dell'import storico non è fra le persone: si mostra «Import storico»
    // sensitive-ok: solo per sapere se la persona esiste; si mostra con people.show
    const nm = v ? people.name(v.uploadedById) : "";
    const who = nm && nm !== "—" ? nm : /\nOrigine: /.test(e.description ?? "") ? t("f_importBy") : nm;
    return (
      <div key={e.id} style={{ display: "flex", gap: 12, alignItems: "center", padding: "8px 10px", border: `1px solid ${palette.hair}`, borderRadius: 8 }}>
        {icon(e.name)}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Button type="link" style={{ padding: 0, height: "auto", maxWidth: "100%" }} onClick={() => setOpenId(e.id)}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.name}</span>
          </Button>
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {prov || label(e.target)}
              {auditCode(e.auditId) ? ` · ${auditCode(e.auditId)}` : ""}
              {v ? ` · v${v.v} · ${fmtBytes(v.size)}` : ""}
            </Typography.Text>
          </div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {v ? <>{nm && nm !== "—" ? people.show(v.uploadedById) : who || "—"} · {fmtDT(v.createdAt)}</> : ""}
          </Typography.Text>
        </div>
        <EvidenceStatusPill s={e.status} />
      </div>
    );
  };
  const list =
    evidence.length === 0 ? (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("none")} />
    ) : (
      <Space direction="vertical" style={{ width: "100%" }} size={8}>
        {grouped
          ? groups.map((g) => (
              <div key={g.role} style={{ display: "grid", gap: 6 }}>
                <Typography.Text strong style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5, color: palette.muted }}>
                  {t(`f_role_${g.role}`)} ({g.items.length})
                </Typography.Text>
                {g.items.map(row)}
              </div>
            ))
          : evidence.map(row)}
      </Space>
    );
  if (bare)
    return (
      <>
        {list}
        <EvidenceDrawer evidence={live} onClose={() => setOpenId(null)} onChanged={onChanged} />
      </>
    );

  return (
    <Card
      styles={{ body: { padding: mobile ? 12 : 24 } }}
      title={
        <span>
          {t("evidence")} <Typography.Text type="secondary">({evidence.length})</Typography.Text>
        </span>
      }
      extra={
        canUpload ? (
          <Button type="primary" size={mobile ? "large" : "small"} icon={<UploadOutlined />} onClick={() => setUp(true)}>
            {t("upload")}
          </Button>
        ) : null
      }
    >
      {list}
      <UploadModal open={up} onClose={() => setUp(false)} ctx={ctx} onDone={() => onChanged?.()} />
      <EvidenceDrawer evidence={live} onClose={() => setOpenId(null)} onChanged={onChanged} />
    </Card>
  );
}
